import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import ConfigJson from "../config.json";
import ConfigSDF from './utils/sdf_config.json';
import SDFStructure from "./components/SDFStructure";
import SDFLegacyStructure from "./components/SDFLegacyStructure";
import Logo from "./../img/natura2000_logo.svg";
import {
    Select,
    Message,
    Loader
} from "semantic-ui-react"

const SDF = () => {

    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [errorLoading, setErrorLoading] = useState(false);
    const [siteCode, setSiteCode] = useState("");
    const [release, setRelease] = useState("");
    const [releases, setReleases] = useState([]);
    const [nav, setNav] = useState("");
    const [showScrollBtn, setShowScrollBtn] = useState(false);

    useEffect(() => {
        if(!siteCode) {
            getSiteCode();
        }
    }, [searchParams]);

    useEffect(() => {
        if( !isLoading && siteCode && siteCode !== "nodata" && Object.keys(data).length === 0 && !errorLoading) {
            loadData();
        }
    }, [siteCode, release]);

    useEffect(() => {
        window.addEventListener("scroll", () => {
            if(window.scrollY === 0) {
                setShowScrollBtn(false);
            }
            else {
                setShowScrollBtn(true);
            }
        });
        if (!document.title.includes(siteCode)) {
            document.title += " - " + siteCode;
        }
    }, []);

    useEffect(() => {
        if(nav && !isLoading && siteCode && siteCode !== "nodata" && data !== "nodata" && !errorLoading) {
            scrollTo(nav);
        }
    }, [isLoading]);

    const getSiteCode = () => {
        let params = Object.fromEntries([...searchParams]);
        setSiteCode(params.site ? params.site : "nodata");
        setRelease(params.release ? parseInt(params.release) : "");
        setNav(params.nav);
    }

    const loadData = () => {
        if(siteCode !== "" && !isLoading) {
            setIsLoading(true);
            if(release) {
                fetchSDF(release);
            }
            else {
                let url = ConfigJson.GetLastReleaseId + "?siteCode=" + siteCode + "&sdfPublic=true";
                fetch(url)
                .then(response => response.json())
                .then(releaseData => {
                    if(releaseData?.Success) {
                        fetchSDF(releaseData.Data);
                    }
                    else {
                        setErrorLoading(true);
                        setIsLoading(false);
                    }
                });
            }
        }
        else {
            setData("nodata");
            setIsLoading(false);
        }
    }

    const fetchSDF = (lastRelease) => {
        const isLegacy = lastRelease < 100;
        let url = ConfigJson.PublicSDF + (isLegacy ? "Legacy" : "") + "?sdfPublic=true";
        url += "&siteCode=" + siteCode;
        if(lastRelease) {
            url += "&releaseId=" + lastRelease;
        }
        fetch(url)
        .then(response => response.json())
        .then(data => {
            if(data?.Success) {
                if(!data.Data.SiteInfo.SiteCode) {
                    setData("nodata");
                }
                else {
                    let releases = data.Data.SiteInfo.Releases.sort((a, b) => new Date(b.ReleaseDate) - new Date(a.ReleaseDate));
                    setReleases(releases);
                    setData(isLegacy ? formatData(data): data.Data);
                    if(!release) {
                        let release = releases[0].ReleaseId;
                        setRelease(release);
                        setSearchParams({"site": siteCode, "release": release});
                    }
                }
            }
            else {
                setErrorLoading(true);
            }
            setIsLoading(false);
        });
    }

    const formatData = (data) => {
        let siteCentre = Object.fromEntries(Object.entries(data.Data.SiteLocation).filter(([key, value]) => key === "Latitude" || key === "Longitude"));
        data.Data.SiteLocation.Longitude = siteCentre;
        delete data.Data.SiteLocation.Latitude;
        let siteCharacter = Object.fromEntries(Object.entries(data.Data.SiteDescription).filter(([key, value]) => key === "GeneralCharacter" || key === "OtherCharacteristics"));
        data.Data.SiteDescription.GeneralCharacter = siteCharacter;
        delete data.Data.SiteDescription.OtherCharacteristics;
        let threats = Object.fromEntries(Object.entries(data.Data.SiteDescription).filter(([key, value]) => key === "NegativeThreats" || key === "PositiveThreats"));
        data.Data.SiteDescription.NegativeThreats = threats;
        delete data.Data.SiteDescription.PositiveThreats;
        let documents = Object.fromEntries(Object.entries(data.Data.SiteDescription).filter(([key, value]) => key === "Documents" || key === "Links"));
        data.Data.SiteDescription.Documents = documents;
        delete data.Data.SiteDescription.Links;
        return data.Data;
    }

    const changeRelease = (event, release) => {
        setSearchParams(searchParams => {
            searchParams.set("release", release.value);
            searchParams.delete("nav");
            return searchParams;
        });
        setSiteCode("");
        setRelease("");
        setData([]);
        setErrorLoading(false);
    }

    const scrollTo = (item, e = null) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        const element = document.getElementById(item);
        if (!element) return;
        const y = element.getBoundingClientRect().top + window.scrollY;
        const baseUrl = window.location.href.split("&nav")[0];
        window.history.pushState(null, null, `${baseUrl}&nav=${item}`);
        window.scroll({
            top: y,
            behavior: 'instant'
        });
    }

    const formatDate = (date, ddmmyyyy) => {
        date = new Date(date);
        var d = date.getDate();
        var m = date.getMonth() + 1;
        var y = date.getFullYear();
        if(ddmmyyyy) {
            date = (d <= 9 ? "0" + d : d) + "/" + (m <= 9 ? "0" + m : m) + "/" + y;
        }
        else {
            date = (y + "-" + (m <= 9 ? "0" + m : m));
        }
        return date;
    };

    return (
        <div className="main">
            <div className="ui basic segment">
                <main>
                    <div id="view">
                        <div id="page-document" className="ui container">
                            <div className="sdf">
                                <div className="container--main min-vh-100">
                                    <div className="ui container sdf-header">
                                        <div className="ui grid py-4">
                                            <div className="sdf-head">
                                                <div className="logo">
                                                    <a title="Site logo" className="logo" href="/.">
                                                        <img title="Site" src={Logo} alt="Natura 2000" className="ui image eea-logo" />
                                                    </a>
                                                </div>
                                                <div>
                                                    <h1>NATURA 2000 - STANDARD DATA FORM</h1>
                                                    {release && releases.length > 0 && <b>RELEASE {releases.find(a => a.ReleaseId === release)?.ReleaseName} ({formatDate(releases.find(a => a.ReleaseId === release)?.ReleaseDate, true)})</b>}
                                                    {
                                                        !isLoading && siteCode && siteCode !== "nodata" && data !== "nodata" && Object.keys(data).length > 0 && !errorLoading &&
                                                        <h2>{data.SiteInfo.SiteName} ({data.SiteInfo.SiteCode} - {ConfigSDF.SiteType[data.SiteInfo.Directive ?? data.SiteInfo.SiteType]})</h2>
                                                    }
                                                </div>
                                                <div className="select--right">
                                                    <Select
                                                        placeholder="Select a release"
                                                        name="release"
                                                        options=
                                                            {
                                                                releases && releases.map((item, i) => (
                                                                    {
                                                                        key: item.ReleaseId, value: item.ReleaseId, text: (item.ReleaseName + " (" + formatDate(item.ReleaseDate, true) + ")")
                                                                    }
                                                                ))
                                                            }
                                                        value={releases.find(a => a.ReleaseId === release) ? release : ""}
                                                        onChange={changeRelease}
                                                        selectOnBlur={false}
                                                        loading={isLoading}
                                                        disabled={isLoading || errorLoading || siteCode === "nodata"}
                                                    />
                                                    {
                                                        !isLoading && siteCode && siteCode !== "nodata" && data !== "nodata" && Object.keys(data).length > 0 && !errorLoading &&
                                                        <div className="sdf-download">
                                                            <button className="ui button secondary" onClick={() => { window.print() }}>
                                                                <i className="icon ri-download-line"></i> Download PDF
                                                            </button>
                                                        </div>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {(errorLoading && !isLoading) &&
                                        <div className="ui container">
                                            <div className="ui grid py-4">
                                                <Message error>
                                                    <i className="triangle exclamation icon"></i>
                                                    Error loading data
                                                </Message>
                                            </div>
                                        </div>
                                    }
                                    {isLoading ?
                                        <Loader active={isLoading} inline="centered" className="my-6" />
                                        :
                                        siteCode === "nodata" || data === "nodata" ? <div className="nodata-container"><em>No Data</em></div> :
                                            siteCode && Object.keys(data).length > 0 &&
                                            <>
                                                {release < 100 ?
                                                    <SDFLegacyStructure
                                                        data={data}
                                                        siteCode={siteCode}
                                                        release={release}
                                                        formatDate={formatDate}
                                                        mapUrl={ConfigJson.MapReleases}
                                                    ></SDFLegacyStructure>
                                                    :
                                                    <SDFStructure
                                                        data={data}
                                                        siteCode={siteCode}
                                                        release={release}
                                                        formatDate={formatDate}
                                                        mapUrl={ConfigJson.MapReleases}
                                                    ></SDFStructure>
                                                }
                                            </>
                                    }
                                    {showScrollBtn &&
                                        <div className="sdf-scroll">
                                            <button className="ui button secondary" onClick={() => window.scroll({ top: 0, behavior: "instant" })}>
                                                <i aria-hidden="true" className="ri-arrow-up-s-line"></i>
                                            </button>
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default SDF;
